import { useState, useEffect, useCallback, Fragment } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Wrench, ChevronRight, ChevronDown, ExternalLink, AlertCircle } from 'lucide-react';
import api from '@/lib/api';

const FDNY_PORTAL_URL = 'https://fires.fdnycloud.org/';

const SOURCE_BADGE = {
  DOT: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  FDNY: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  Elevator: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  Boiler: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  Facade: 'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30',
};

const DETAIL_LABELS = {
  permit_number: 'Permit #', description: 'Description', cost: 'Cost', status: 'Status',
  status_detail: 'Status Detail', expiration_date: 'Expiration', issue_date: 'Issue Date',
  permittee_name: 'Permittee', dataset_source: 'Dataset', last_fetched: 'Last Fetched',
  record_type: 'Record Type', record_number: 'Record #', notes: 'Notes', fdny_permit_type: 'Permit Type',
  device_number: 'Device #', device_type: 'Device Type', device_status: 'Device Status',
  status_date: 'Status Date', periodic_latest_inspection: 'Latest Inspection', cat1_latest_report_filed: 'CAT1 Report Filed',
  tracking_number: 'Tracking #', boiler_id: 'Boiler ID', report_status: 'Report Status', inspection_date: 'Inspection Date',
  control_no: 'Control #', tr6_no: 'TR6 #', filing_status: 'Filing Status', current_status: 'Current Status', cycle: 'Cycle',
  source: 'Source', dataset_id: 'Dataset',
};
const HIDDEN_DETAIL_KEYS = new Set(['id', 'project_id']);

function labelFor(k) {
  return DETAIL_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function ExpiryTag({ flag }) {
  if (!flag) return null;
  const expired = flag === 'expired';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${expired ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30' : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'}`}>
      <AlertCircle className="h-2.5 w-2.5" /> {expired ? 'Expired' : 'Expiring Soon'}
    </span>
  );
}

export function CompliancePermitsTab({ onOpenProject, mismatchProjectIds = new Set() }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ active: 0, expiring: 0, unverified: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [cardFilter, setCardFilter] = useState('all'); // all | active | expiring | unverified
  const [expanded, setExpanded] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/compliance/permits-devices');
      setRows(r.data.rows || []);
      setSummary(r.data.summary || { active: 0, expiring: 0, unverified: 0 });
    } catch { setRows([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = rows.filter(v => {
    if (sourceFilter !== 'all' && v.source !== sourceFilter) return false;
    if (cardFilter === 'active' && !v.in_good_standing) return false;
    if (cardFilter === 'expiring' && !v.expiry_flag) return false;
    if (cardFilter === 'unverified' && !v.unverified) return false;
    if (search) {
      const q = search.toLowerCase();
      return (v.project_number || '').toLowerCase().includes(q)
        || (v.project_name || '').toLowerCase().includes(q)
        || (v.address || '').toLowerCase().includes(q)
        || (v.number || '').toLowerCase().includes(q)
        || (v.source || '').toLowerCase().includes(q)
        || (v.source_full || '').toLowerCase().includes(q);
    }
    return true;
  });

  const toggleCard = (s) => setCardFilter(prev => (prev === s ? 'all' : s));

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">Loading permits & devices...</p>;

  if (rows.length === 0) {
    return (
      <Card className="border-border bg-card shadow-none">
        <CardContent className="py-12 text-center">
          <Wrench className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No permits or devices recorded across any project yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Add DOT permits, FDNY records, or discover elevator/boiler/facade records per-project on the Building/Violations Profile tab.</p>
        </CardContent>
      </Card>
    );
  }

  const cards = [
    { key: 'active', label: 'Active / Current', value: summary.active, dot: 'bg-emerald-400', active: cardFilter === 'active' },
    { key: 'expiring', label: 'Expiring Soon / Expired', value: summary.expiring, dot: 'bg-amber-400', active: cardFilter === 'expiring' },
    { key: 'unverified', label: 'Manually Entered / Unverified (FDNY)', value: summary.unverified, dot: 'bg-orange-400', active: cardFilter === 'unverified' },
  ];

  return (
    <div className="space-y-3">
      {/* Summary cards (click to filter) */}
      <div className="grid grid-cols-3 gap-2">
        {cards.map(c => (
          <Card
            key={c.key}
            className={`border-border shadow-none cursor-pointer transition-colors hover:border-primary/30 ${c.active ? 'border-primary/50 bg-primary/5' : 'bg-card'}`}
            onClick={() => toggleCard(c.key)}
            data-testid={`permits-card-${c.key}`}
          >
            <CardContent className="p-2.5 text-center">
              <div className={`h-2 w-2 rounded-full mx-auto mb-1 ${c.dot}`} />
              <p className="text-lg font-bold">{c.value}</p>
              <p className="text-xs text-muted-foreground leading-tight">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search + source filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search project, address, source, #..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border w-72" data-testid="permits-search" />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-40 bg-card border-border text-xs h-9" aria-label="Filter by source" data-testid="permits-source-filter">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Sources</SelectItem>
            <SelectItem value="DOT" className="text-xs">DOT</SelectItem>
            <SelectItem value="FDNY" className="text-xs">FDNY</SelectItem>
            <SelectItem value="Elevator" className="text-xs">Elevator</SelectItem>
            <SelectItem value="Boiler" className="text-xs">Boiler</SelectItem>
            <SelectItem value="Facade" className="text-xs">Facade</SelectItem>
          </SelectContent>
        </Select>
        {(search || sourceFilter !== 'all' || cardFilter !== 'all') && (
          <button className="text-xs text-primary hover:underline ml-1" onClick={() => { setSearch(''); setSourceFilter('all'); setCardFilter('all'); }}>Clear filters</button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} of {rows.length} records</span>
      </div>

      {/* Combined table */}
      <Card className="border-border bg-card shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Address</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Source</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Record / Permit / Device #</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Expiration / Inspection Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-xs py-8">No records match your filters.</TableCell></TableRow>
              ) : filtered.map((v, i) => {
                const open = expanded === i;
                return (
                  <Fragment key={i}>
                    <TableRow className="border-border hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => setExpanded(open ? null : i)} data-testid={`permits-row-${i}`}>
                      <TableCell className="text-xs">
                        <span className="inline-flex items-center gap-1 flex-wrap">
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                          <span className="font-mono text-primary">{v.project_number || '—'}</span>
                          <span className="text-muted-foreground ml-1 hidden lg:inline truncate max-w-[150px]">{v.project_name}</span>
                          {mismatchProjectIds.has(v.project_id) && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-amber-400/90 border border-amber-500/30 rounded px-1.5 py-0.5 font-sans whitespace-nowrap" title="This project's BIN may not match its address on file" data-testid={`permits-bin-mismatch-${i}`}>
                              <AlertCircle className="h-2.5 w-2.5" /> BIN mismatch
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{v.address || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs border ${SOURCE_BADGE[v.source] || 'border-border text-muted-foreground'}`} data-testid={`permits-source-${i}`}>{v.source}</Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          {v.number || '-'}
                          {v.portal_link && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-orange-400/90 border border-orange-500/30 rounded px-1.5 py-0.5 font-sans" data-testid={`permits-portal-note-${i}`}>
                              <ExternalLink className="h-2.5 w-2.5" /> Portal-verify
                            </span>
                          )}
                          {v.unverified && !v.portal_link && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-amber-400/90 border border-amber-500/30 rounded px-1.5 py-0.5 font-sans" data-testid={`permits-unverified-note-${i}`}>
                              <AlertCircle className="h-2.5 w-2.5" /> Unverified
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        {v.status
                          ? <Badge variant="outline" className={`text-xs border ${v.in_good_standing ? 'border-emerald-500/40 text-emerald-400' : 'border-border text-muted-foreground'}`} data-testid={`permits-status-${i}`}>{v.status}</Badge>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          <span className="text-muted-foreground">{v.date || '—'}</span>
                          <ExpiryTag flag={v.expiry_flag} />
                        </span>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className="border-border bg-secondary/20 hover:bg-secondary/20">
                        <TableCell colSpan={6} className="p-0">
                          <div className="px-4 py-3" data-testid={`permits-detail-${i}`}>
                            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{v.source_full} — Record Detail</span>
                              <div className="flex items-center gap-3">
                                {v.portal_link && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); window.open(FDNY_PORTAL_URL, '_blank', 'noopener,noreferrer'); }}
                                    className="inline-flex items-center gap-1 text-xs text-orange-400 hover:underline"
                                    data-testid={`permits-portal-link-${i}`}
                                  >
                                    <ExternalLink className="h-3 w-3" /> Verify on FDNY Portal
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); onOpenProject?.(v.project_id, 'building-profile'); }}
                                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                  data-testid={`permits-view-in-project-${i}`}
                                >
                                  <ExternalLink className="h-3 w-3" /> View in Project
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                              {Object.entries(v.detail || {})
                                .filter(([k, val]) => !HIDDEN_DETAIL_KEYS.has(k) && val !== null && val !== '' && val !== undefined)
                                .map(([k, val]) => (
                                  <div key={k} className="flex gap-2 text-xs">
                                    <span className="text-muted-foreground shrink-0 min-w-[120px]">{labelFor(k)}</span>
                                    <span className="text-foreground break-words">{String(val)}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
